import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, Edit2 } from "lucide-react";
import { HeadlineGroup, Insight, Headline } from '@/types';
import { useStore } from '@/store';
import { SummaryPopover } from '@/components/shared/SummaryPopover';
import { Button } from "@/components/ui/button";
import { generateQuestions, generateQnA } from "@/api/api";
import { useToast } from "@/hooks/use-toast";
import { HeadlinePopover } from '@/components/shared/HeadlinePopover';
import { HeadlineCircle } from '@/components/shared/HeadlineCircle';
import { Checkbox } from "@/components/ui/checkbox";
import { Question } from '@/components/panel/Question';

// Create a TableRowContent component for consistent column alignment
interface TableRowContentProps {
  group: HeadlineGroup;
  isExpanded: boolean;
  onToggle: () => void;
  headlines: Headline[];
  insights: Insight[];
}

// Update TableRowContent to work better with popover
function TableRowContent({ group, isExpanded, onToggle, headlines, insights: initialInsights }: TableRowContentProps) {
  const { 
    selectedDots, 
    toggleDotSelection,
    selectedStreamId,
    selectedWorkspaceId,
    insights: storeInsights  // Get insights from store
  } = useStore();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const groupHeadlines = headlines?.filter(headline => 
    headline.headline_group_id === group.headline_group_id
  ) || [];
  
  const visibleHeadlines = groupHeadlines.slice(0, 3);
  const expandableHeadlines = groupHeadlines.slice(3);
  
  const isSelected = selectedDots.some(
    dot => dot.id === group.headline_group_id && dot.type === 'headline_group'
  );
  
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleRowClick = () => {
    onToggle();
  };
  
  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleGenerateQuestions = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!selectedStreamId || !selectedWorkspaceId) {
      toast({
        title: "Error",
        description: "No stream or workspace selected",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    
    try {
      const result = await generateQuestions(
        selectedStreamId,
        selectedWorkspaceId,
        group.headline_group_id
      );
      
      if (result.success) {
        // No need to manually update state as Shape subscription will handle it
        toast({
          title: "Success",
          description: "Generated new questions successfully",
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to generate questions",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate questions",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleGenerateQnA = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!selectedStreamId || !selectedWorkspaceId) {
      toast({
        title: "Error",
        description: "No stream or workspace selected",
        variant: "destructive",
      });
      return;
    }

    // Get selected questions from the store
    const selectedQuestions = selectedDots
      .filter(dot => dot.type === 'question')
      .map(dot => dot.name);

    if (selectedQuestions.length === 0) {
      toast({
        title: "Error", 
        description: "Please select at least one question",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await generateQnA(
        selectedStreamId,
        selectedWorkspaceId,
        [group.headline_group_id], // Using current group ID
        selectedQuestions
      );
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Generated QnA successfully",
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to generate QnA",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate QnA",
        variant: "destructive",
      });
    }
  };
  
  // Get questions from all relevant insights for this headline group
  const relevantInsights = storeInsights.filter(insight => 
    insight.headline_group_ids?.includes(group.headline_group_id) &&
    insight.is_questions
  );

  return (
    <>
      <TableRow 
        className={`hover:bg-muted/50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
        onClick={handleRowClick}
      >
        <TableCell className="py-2 px-4 w-full" style={{ minWidth: "0" }}>
          <div className="flex items-center justify-between w-full min-w-0">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {/* Add checkbox */}
              <div onClick={e => e.stopPropagation()}>
                <Checkbox 
                  checked={isSelected}
                  onCheckedChange={() => {
                    toggleDotSelection({
                      id: group.headline_group_id,
                      type: 'headline_group',
                      name: group.headline_group_name
                    });
                  }}
                />
              </div>

              {/* Show headlines with popover */}
              <div className="flex-shrink-0">
                <HeadlinePopover headlines={groupHeadlines}>
                  <div className="flex -space-x-2">
                    {visibleHeadlines.map((headline: Headline) => (
                      <HeadlineCircle key={headline.content_id} headline={headline} />
                    ))}
                  </div>
                </HeadlinePopover>
              </div>

              <div className="min-w-0 flex-1">
                <SummaryPopover summary={group.summary}>
                  <div className="overflow-hidden relative group">
                    <div className="flex whitespace-nowrap group-hover:animate-marquee">
                      <span className="pr-4">{group.headline_group_name}</span>
                      <span className="pr-4">{group.headline_group_name}</span>
                      <span className="pr-4">{group.headline_group_name}</span>
                    </div>
                  </div>
                </SummaryPopover>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(e);
                }}
                className="p-1 hover:bg-muted rounded-sm transition-colors"
                title="Edit in workspace"
              >
                <Edit2 className="h-4 w-4 text-muted-foreground" />
              </button>
              <ChevronDown 
                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                  isExpanded ? '' : 'rotate-180'
                }`}
              />
            </div>
          </div>
        </TableCell>
      </TableRow>

      {/* Show expanded content */}
      {isExpanded && (
        <>
          {relevantInsights.map((insight: Insight) => {
            const questions = parseQuestions(insight);
            const questionTexts = questions.map(q => q.question);
            
            return questions.map((question, questionIndex) => (
              <TableRow key={`${insight.insight_id}-${questionIndex}`} className="bg-muted/5">
                <TableCell className="py-0.5 px-8">
                  <Question
                    key={`${insight.insight_id}-${questionIndex}`}
                    id={question.id}
                    question={question.question}
                    insightId={insight.insight_id}
                    allQuestions={questionTexts}
                    questionIndex={questionIndex}
                    onQuestionsUpdate={(updatedQuestions) => {
                      // Update will be handled by Shape subscription
                      // Just call the API and let Shape handle the re-render
                    }}
                  />
                </TableCell>
              </TableRow>
            ));
          })}

          {/* More Questions button */}
          <TableRow className="bg-muted/5">
            <TableCell className="py-1.5 px-16">
              <div className="flex gap-2">
                <Button
                  onClick={handleGenerateQuestions}
                  disabled={isGenerating}
                  className="text-sm h-6 px-3"
                  variant="ghost"
                >
                  {isGenerating ? "Generating..." : "More Questions!"}
                </Button>
                
                {/* Add Generate QnA button */}
                <Button
                  onClick={handleGenerateQnA}
                  className="text-sm h-6 px-3"
                  variant="ghost"
                >
                  Generate QnA
                </Button>
              </div>
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
}

// Add new interface for parsed questions
interface ParsedQuestion {
  id: string; // Using the insight_id + index as unique identifier
  question: string;
}

// Update helper function to parse questions from text_main with debugging
function parseQuestions(insight: Insight): ParsedQuestion[] {
  if (!insight) {
    return [];
  }
  
  if (!insight.text_main) {
    return [];
  }
  
  const lines = insight.text_main.split('\n');
  
  const questions = lines
    .filter(line => line.trim()) // Remove empty lines
    .map((line, index) => {
      const question = line.replace(/^[\d•\-\s.]*/, '').trim();
      return {
        id: `${insight.insight_id}-${index}`,
        question: question
      };
    });
    
  return questions;
}

// Add new DateGroupRow component
interface DateGroupRowProps {
  date: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function DateGroupRow({ date, isExpanded, onToggle }: DateGroupRowProps) {
  return (
    <TableRow 
      className="cursor-pointer bg-muted/30"
      onClick={onToggle}
    >
      <TableCell className="py-2 px-4">
        <div className="flex items-center justify-between w-full">
          <span className="font-medium">{date}</span>
          <ChevronDown 
            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
              isExpanded ? '' : 'rotate-180'
            }`}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// Add new interface for grouped headlines
interface GroupedHeadlines {
  [date: string]: HeadlineGroup[];
}

// Add helper function to group headlines by date
function groupHeadlinesByDate(headlines: HeadlineGroup[]): GroupedHeadlines {
  return headlines.reduce((groups: GroupedHeadlines, headline) => {
    const date = new Date(headline.created_at).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(headline);
    return groups;
  }, {});
}

interface NewsStreamProps {
  onHeadlineGroupSelect: (group: HeadlineGroup) => void;
}

export default function NewsStream({ onHeadlineGroupSelect }: NewsStreamProps) {
  const {
    headlineGroups,
    headlines,
    insights,
    selectedHeadlineGroup,
    setSelectedHeadlineGroup,
    setError,
    expandedDates,
    expandedRows,
    toggleExpandedDate,
    toggleExpandedRow,
    setExpandedDates,
    setExpandedRows
  } = useStore();

  // Add window size tracking
  const [containerHeight, setContainerHeight] = useState(0);

  // Initialize expandedDates with only the first date expanded
  useEffect(() => {
    const groupedHeadlines = groupHeadlinesByDate(headlineGroups);
    const sortedDates = Object.keys(groupedHeadlines).sort((a, b) => 
      new Date(b).getTime() - new Date(a).getTime()
    );
    
    // Only expand the first date if we have dates and expandedDates is empty
    if (sortedDates.length > 0 && expandedDates.size === 0) {
      const newExpandedDates = new Set<string>();
      newExpandedDates.add(sortedDates[0]);
      setExpandedDates(newExpandedDates);
    }
  }, [headlineGroups, expandedDates.size, setExpandedDates]);

  // Add resize handler
  useEffect(() => {
    const updateHeight = () => {
      // Assuming SelectionHeader is approximately 64px high
      // Adjust this value based on your actual header height
      const headerHeight = 64;
      setContainerHeight(window.innerHeight - headerHeight);
    };

    // Set initial height
    updateHeight();

    // Add resize listener
    window.addEventListener('resize', updateHeight);

    // Cleanup
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleRowToggle = (group: HeadlineGroup) => {
    try {
      toggleExpandedRow(group.headline_group_id);
      setSelectedHeadlineGroup(group);
      onHeadlineGroupSelect(group);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to toggle row'));
    }
  };

  const handleDateToggle = (date: string) => {
    toggleExpandedDate(date);
  };

  const groupedHeadlines = groupHeadlinesByDate(headlineGroups);
  const sortedDates = Object.keys(groupedHeadlines).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div 
      className="overflow-y-auto"
      style={{ height: `${containerHeight}px` }}
    >
      <div className="inline-block min-w-full">
        <Table className="min-w-full table-fixed">
          <TableBody>
            {headlineGroups.length > 0 ? (
              sortedDates.map(date => (
                <React.Fragment key={date}>
                  <DateGroupRow 
                    date={date}
                    isExpanded={expandedDates.has(date)}
                    onToggle={() => handleDateToggle(date)}
                  />
                  
                  {expandedDates.has(date) && (
                    groupedHeadlines[date].map((group: HeadlineGroup) => {
                      const isExpanded = expandedRows.has(group.headline_group_id);
                      
                      return (
                        <React.Fragment key={group.headline_group_id}>
                          <TableRowContent 
                            group={group}
                            isExpanded={isExpanded}
                            onToggle={() => handleRowToggle(group)}
                            headlines={headlines}
                            insights={insights}
                          />
                        </React.Fragment>
                      );
                    })
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="py-8">
                  <div className="flex flex-col items-center justify-center h-[200px] text-gray-500 space-y-2">
                    <p>No headline groups available for this stream</p>
                    <p className="text-sm text-gray-400">Select another stream or wait for new headlines</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 